import type { Job, JobAttempt } from '../../lib/schemas'
import { useJobAttempts } from '../../hooks/useJobAttempts'
import { TESTID } from '../../lib/testids'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card'

const statuses: Record<JobAttempt['status'], string> = {
  provisioning: 'Przygotowywanie',
  running: 'W trakcie',
  succeeded: 'Sukces',
  failed: 'Błąd',
  cancelled: 'Anulowana',
  interrupted: 'Przerwana',
}
const phases: Record<string, string> = {
  retry_pending: 'Oczekiwanie na rezerwację maszyny do ponowienia',
  retry_running: 'Ponowienie na tej samej maszynie',
  reboot_pending: 'Oczekiwanie na restart komputera',
  reboot_wait: 'Oczekiwanie na nowy start Windows i odblokowany pulpit',
  post_reboot_running: 'Ostatnia próba po restarcie komputera',
  finishing: 'Kończenie odzyskiwania i zwalnianie rezerwacji maszyny',
  finished: 'Odzyskiwanie zakończone',
}
const dateTime = (iso: string) => new Date(iso).toLocaleString('pl-PL')

export function JobAttempts({ job }: { job: Job }) {
  const { data, isPending, isError, refetch } = useJobAttempts(
    job.id,
    job.status,
  )
  return (
    <Card size="sm" data-testid={TESTID.jobAttempts}>
      <CardHeader>
        <CardTitle>
          <h3>Próby wykonania{data ? ` (${data.attempts.length})` : ''}</h3>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {isPending && (
            <p role="status" className="text-muted-foreground">
              Ładowanie historii prób…
            </p>
          )}
          {isError && (
            <div role="alert" className="space-y-2">
              <p>Nie udało się pobrać aktualnej historii prób.</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void refetch()}
              >
                Spróbuj ponownie
              </Button>
            </div>
          )}
          {data?.recovery && (
            <div className="space-y-1 text-sm" data-testid={TESTID.jobRecovery}>
              <p className="font-medium">
                {phases[data.recovery.phase] ?? data.recovery.phase}
              </p>
              <p className="text-muted-foreground">
                Wystąpienia błędu uruchamiającego retry: {data.recovery.failure_count}
              </p>
              {data.recovery.last_error && (
                <p className="break-words text-destructive">
                  Ostatni błąd sterowania maszyną: {data.recovery.last_error}
                </p>
              )}
            </div>
          )}
          {data?.attempts.length === 0 && (
            <p className="text-muted-foreground">
              Ten job nie ma jeszcze prób wykonania.
            </p>
          )}
          <ol className="space-y-4">
            {data?.attempts.map((attempt) => (
              <li
                key={attempt.id}
                data-testid={TESTID.jobAttempt(attempt.id)}
                className="space-y-2 border-t border-border pt-3"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">
                    Próba {attempt.attempt_number}
                  </span>
                  <Badge
                    variant={
                      attempt.status === 'failed' ? 'destructive' : 'secondary'
                    }
                  >
                    {statuses[attempt.status]}
                  </Badge>
                </div>
                <dl className="space-y-1 text-xs">
                  <div>
                    <dt className="inline text-muted-foreground">Start: </dt>
                    <dd className="inline">{dateTime(attempt.started_at)}</dd>
                  </div>
                  <div>
                    <dt className="inline text-muted-foreground">Koniec: </dt>
                    <dd className="inline">
                      {attempt.completed_at
                        ? dateTime(attempt.completed_at)
                        : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="inline text-muted-foreground">Maszyna: </dt>
                    <dd className="inline break-all">
                      {attempt.machine_id ?? 'Brak danych'}
                    </dd>
                  </div>
                  {attempt.worker_version && (
                    <div>
                      <dt className="inline text-muted-foreground">Worker: </dt>
                      <dd className="inline">{attempt.worker_version}</dd>
                    </div>
                  )}
                  {attempt.exit_code != null && (
                    <div>
                      <dt className="inline text-muted-foreground">
                        Kod wyjścia:{' '}
                      </dt>
                      <dd className="inline">{attempt.exit_code}</dd>
                    </div>
                  )}
                  {attempt.execution_id && (
                    <div className="break-all">
                      <dt className="inline text-muted-foreground">
                        Wykonanie:{' '}
                      </dt>
                      <dd className="inline">{attempt.execution_id}</dd>
                    </div>
                  )}
                </dl>
                {(attempt.error_type || attempt.error_message) && (
                  <p className="whitespace-pre-wrap break-words text-xs text-destructive">
                    {[attempt.error_type, attempt.error_message]
                      .filter(Boolean)
                      .join(': ')}
                  </p>
                )}
              </li>
            ))}
          </ol>
        </div>
      </CardContent>
    </Card>
  )
}
